// Chinese Character Utility Functions
// Handles character preference for category titles and content display

/**
 * Get the appropriate title based on Chinese character preference
 * @param {Object} set - Vocabulary set object
 * @param {string} preference - Character preference: 'traditional', 'simplified', 'mixed'
 * @returns {string} - The appropriate title
 */
export const getTitleByPreference = (set, preference) => {
  switch (preference) {
    case 'traditional':
      return set.titleTraditional || set.title;
    case 'simplified':
      return set.titleSimplified || set.title;
    case 'mixed':
    default:
      return set.title; // Use original title as is
  }
};

/**
 * Get the appropriate category name based on Chinese character preference
 * @param {Object} set - Vocabulary set object
 * @param {string} preference - Character preference: 'traditional', 'simplified', 'mixed'
 * @returns {string} - The appropriate category name
 */
export const getCategoryByPreference = (set, preference) => {
  switch (preference) {
    case 'traditional':
      return set.categoryTraditional || set.category;
    case 'simplified':
      return set.categorySimplified || set.category;
    case 'mixed':
    default:
      return set.category; // Use original category as is
  }
};

/**
 * Get the appropriate word text based on Chinese character preference and language display
 * @param {Object} word - Word object
 * @param {string} languageDisplay - Language display setting
 * @param {string} preference - Character preference: 'traditional', 'simplified', 'mixed'
 * @returns {Object} - Object with primary, secondary, and phonetic text
 */
export const getWordTextByPreference = (word, languageDisplay, preference) => {
  // Determine which Chinese text to use based on preference
  const getChineseText = (word, preference) => {
    switch (preference) {
      case 'traditional':
        return word.chineseTraditional;
      case 'simplified':
        return word.chineseSimplified;
      case 'mixed':
      default:
        // For mixed, use the original based on language display
        if (languageDisplay.includes('traditional')) {
          return word.chineseTraditional;
        } else if (languageDisplay.includes('simplified')) {
          return word.chineseSimplified;
        }
        return word.chineseTraditional; // Default fallback
    }
  };

  const chineseText = getChineseText(word, preference);

  switch (languageDisplay) {
    case "traditional_to_english":
      return {
        primary: chineseText,
        secondary: word.english,
        phonetic: word.phonetic
      };
    case "simplified_to_english":
      return {
        primary: chineseText,
        secondary: word.english,
        phonetic: word.phonetic
      };
    case "english_to_traditional":
      return {
        primary: word.english,
        secondary: chineseText,
        phonetic: word.phonetic
      };
    case "english_to_simplified":
      return {
        primary: word.english,
        secondary: chineseText,
        phonetic: word.phonetic
      };
    default:
      return {
        primary: chineseText,
        secondary: word.english,
        phonetic: word.phonetic
      };
  }
};
